import { useState, useEffect } from "react";
import { Save, Loader2, Plus, Trash2, Calendar as CalendarIcon, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { getWorkingHours, updateWorkingHours } from "@/lib/api";

const DAYS = [
  { key: "mon", label: "Pazartesi" },
  { key: "tue", label: "Salı" },
  { key: "wed", label: "Çarşamba" },
  { key: "thu", label: "Perşembe" },
  { key: "fri", label: "Cuma" },
  { key: "sat", label: "Cumartesi" },
  { key: "sun", label: "Pazar" },
];

const TURKEY_HOLIDAYS_2026 = [
  { date: "2026-01-01", name: "Yılbaşı" },
  { date: "2026-03-21", name: "Ramazan Bayramı (1. gün)" },
  { date: "2026-03-22", name: "Ramazan Bayramı (2. gün)" },
  { date: "2026-03-23", name: "Ramazan Bayramı (3. gün)" },
  { date: "2026-04-23", name: "Ulusal Egemenlik ve Çocuk Bayramı" },
  { date: "2026-05-01", name: "Emek ve Dayanışma Günü" },
  { date: "2026-05-19", name: "Atatürk'ü Anma Gençlik ve Spor Bayramı" },
  { date: "2026-05-27", name: "Kurban Bayramı (1. gün)" },
  { date: "2026-05-28", name: "Kurban Bayramı (2. gün)" },
  { date: "2026-05-29", name: "Kurban Bayramı (3. gün)" },
  { date: "2026-05-30", name: "Kurban Bayramı (4. gün)" },
  { date: "2026-07-15", name: "Demokrasi ve Milli Birlik Günü" },
  { date: "2026-08-30", name: "Zafer Bayramı" },
  { date: "2026-10-29", name: "Cumhuriyet Bayramı" },
];

const FALLBACK_SCHEDULE = {
  mon: { open: "09:00", close: "18:00", closed: false },
  tue: { open: "09:00", close: "18:00", closed: false },
  wed: { open: "09:00", close: "18:00", closed: false },
  thu: { open: "09:00", close: "18:00", closed: false },
  fri: { open: "09:00", close: "18:00", closed: false },
  sat: { open: "10:00", close: "16:00", closed: false },
  sun: { open: "", close: "", closed: true },
};

export default function WorkingHoursTab() {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newHoliday, setNewHoliday] = useState({ date: "", name: "" });

  const reload = () => {
    setLoadError(null);
    setData(null);
    getWorkingHours()
      .then((d) => {
        // Backend guarantees these fields but be defensive in case of partial data
        setData({
          schedule: d?.schedule || FALLBACK_SCHEDULE,
          holidays: d?.holidays || [],
          timezone_label: d?.timezone_label || "Europe/Istanbul",
          notes: d?.notes || "",
        });
      })
      .catch((e) => {
        const msg = e?.response?.data?.detail || e?.message || "Çalışma saatleri yüklenemedi.";
        setLoadError(msg);
        toast.error(msg);
      });
  };

  useEffect(() => {
    reload();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await updateWorkingHours(data);
      toast.success("Çalışma saatleri kaydedildi");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydedilemedi");
    } finally { setSaving(false); }
  };

  const updateDay = (key, field, value) => {
    setData({ ...data, schedule: { ...data.schedule, [key]: { ...data.schedule[key], [field]: value } } });
  };

  const addHoliday = () => {
    if (!newHoliday.date) return;
    setData({ ...data, holidays: [...(data.holidays || []), newHoliday] });
    setNewHoliday({ date: "", name: "" });
  };

  const removeHoliday = (i) => {
    setData({ ...data, holidays: data.holidays.filter((_, idx) => idx !== i) });
  };

  const loadTurkey2026 = () => {
    const existing = new Set((data.holidays || []).map((h) => h.date));
    const merged = [...(data.holidays || []), ...TURKEY_HOLIDAYS_2026.filter((h) => !existing.has(h.date))];
    merged.sort((a, b) => a.date.localeCompare(b.date));
    setData({ ...data, holidays: merged });
    toast.success("2026 Türkiye resmi tatilleri eklendi");
  };

  if (loadError) {
    return (
      <Card data-testid="hours-load-error">
        <CardContent className="py-10 text-center space-y-3">
          <div className="text-destructive font-display">Çalışma saatleri yüklenemedi</div>
          <div className="text-xs text-muted-foreground">{loadError}</div>
          <Button variant="outline" size="sm" onClick={reload}>Tekrar dene</Button>
        </CardContent>
      </Card>
    );
  }

  if (!data) return <Loader2 className="w-5 h-5 animate-spin mx-auto" data-testid="hours-loading" />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-lg">Çalışma Saatleri</h2>
          <p className="text-xs text-muted-foreground">AI, randevu sorularında bu bilgileri kullanır.</p>
        </div>
        <Button data-testid="hours-save" onClick={save} disabled={saving} style={{ background: "#B39B7A" }}>
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Kaydet
        </Button>
      </div>

      <Card>
        <CardHeader><CardTitle className="font-display flex items-center gap-2"><Clock className="w-4 h-4" /> Haftalık Program</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {DAYS.map((d) => {
            const sched = data.schedule[d.key] || {};
            return (
              <div key={d.key} data-testid={`day-${d.key}`} className="flex items-center gap-3 p-3 rounded-xl border border-border/40">
                <div className="w-28 font-medium">{d.label}</div>
                <Switch
                  checked={!sched.closed}
                  onCheckedChange={(v) => updateDay(d.key, "closed", !v)}
                  data-testid={`toggle-${d.key}`}
                />
                {sched.closed ? (
                  <Badge variant="secondary" className="text-xs">Kapalı</Badge>
                ) : (
                  <>
                    <Input
                      type="time"
                      value={sched.open || "09:00"}
                      onChange={(e) => updateDay(d.key, "open", e.target.value)}
                      data-testid={`open-${d.key}`}
                      className="w-32"
                    />
                    <span className="text-muted-foreground">→</span>
                    <Input
                      type="time"
                      value={sched.close || "18:00"}
                      onChange={(e) => updateDay(d.key, "close", e.target.value)}
                      data-testid={`close-${d.key}`}
                      className="w-32"
                    />
                  </>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="font-display flex items-center gap-2"><CalendarIcon className="w-4 h-4" /> Resmi Tatil ve Kapalı Günler</CardTitle>
            <Button variant="outline" size="sm" onClick={loadTurkey2026} data-testid="load-2026">
              2026 Türkiye Tatilleri Yükle
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Input
              type="date"
              value={newHoliday.date}
              onChange={(e) => setNewHoliday({ ...newHoliday, date: e.target.value })}
              data-testid="new-holiday-date"
              className="w-44"
            />
            <Input
              placeholder="Tatil adı (örn: Yılbaşı)"
              value={newHoliday.name}
              onChange={(e) => setNewHoliday({ ...newHoliday, name: e.target.value })}
              data-testid="new-holiday-name"
              className="flex-1"
            />
            <Button onClick={addHoliday} data-testid="add-holiday" style={{ background: "#8B9B88" }}>
              <Plus className="w-4 h-4 mr-2" /> Ekle
            </Button>
          </div>
          {(data.holidays || []).length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-4">Henüz tatil yok</div>
          ) : (
            <div className="space-y-2">
              {data.holidays.map((h, i) => (
                <div key={i} data-testid={`holiday-${i}`} className="flex items-center justify-between p-2 rounded-lg border border-border/40">
                  <div className="flex items-center gap-3">
                    <Badge variant="outline" className="font-mono text-xs">{h.date}</Badge>
                    <span className="text-sm">{h.name}</span>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => removeHoliday(i)} className="text-destructive">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="font-display">Ek Notlar</CardTitle></CardHeader>
        <CardContent>
          <Textarea
            data-testid="hours-notes"
            rows={3}
            placeholder="Ör: Öğle arası 13:00-14:00, randevu zorunlu, vs."
            value={data.notes || ""}
            onChange={(e) => setData({ ...data, notes: e.target.value })}
          />
        </CardContent>
      </Card>
    </div>
  );
}
